import React, { useEffect, useState, forwardRef, InputHTMLAttributes } from 'react';

export interface DecimalInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> {
  value: number | undefined;
  onChange: (v: number | undefined) => void;
  precision: number;
  fallbackValue?: number;
  allowNegative?: boolean;
  // When true (default), focusing an empty field with a fallback prefills
  // the fallback into local state AND commits it via onChange so the user
  // can tweak the calculated value as a normal numeric entry. Set to false
  // when committing the fallback would have side effects on the parent
  // form (e.g. flipping a derived mode), so the prefill is purely visual
  // and only an actual edit propagates upward.
  commitFallbackOnFocus?: boolean;
  // Minimum acceptable value (inclusive). On blur, if the committed value
  // is below this threshold (e.g. user typed "0" into a rate field), the
  // input clears local state and emits onChange(undefined) so the canonical
  // fallback / live calc takes over again. Without this, a user-typed "0"
  // would leave the display showing "0" while the parent calc treats 0 as
  // "not set" and uses the live value — a display vs. calc mismatch.
  minValue?: number;
}

const normalizePrecision = (precision: number): number =>
  Number.isFinite(precision) ? Math.max(0, Math.floor(precision)) : 0;

// Plain decimal pattern (no sign) used to gate naturalStr and reject scientific
// notation like "1e+21" / "1e-10" that String(v) returns for very large/small v.
const PLAIN_DECIMAL = /^-?\d+(\.\d+)?$/;

// Format a number for the input. Use the natural String(v) when it parses back
// to the same value (so 1.5 stays "1.5", not "1.500000"). Otherwise truncate
// to precision so the result fits the input regex and is editable. Without
// this, a long-precision live FX rate (e.g. 0.8511217785040683) would render
// in full and the regex would block any keystroke that modified it. naturalStr
// is also rejected if it's scientific notation, since the input regex bans 'e'.
const formatForInput = (v: number | undefined, precision: number): string => {
  if (v === undefined || v === null || !Number.isFinite(v)) return '';
  const p = normalizePrecision(precision);
  const naturalStr = String(v);
  const truncated = v.toFixed(p);
  if (parseFloat(truncated) === v && PLAIN_DECIMAL.test(naturalStr)) return naturalStr;
  return truncated;
};

const isIntermediate = (v: string, allowNegative: boolean): boolean =>
  v === '' ||
  v === '.' ||
  v.endsWith('.') ||
  (allowNegative && (v === '-' || v === '-0' || v === '-.'));

const buildRegex = (precision: number, allowNegative: boolean): RegExp => {
  const p = normalizePrecision(precision);
  const sign = allowNegative ? '-?' : '';
  return new RegExp(`^${sign}\\d*\\.?\\d{0,${p}}$`);
};

export const DecimalInput = forwardRef<HTMLInputElement, DecimalInputProps>(
  (
    {
      value,
      onChange,
      precision,
      fallbackValue,
      allowNegative = false,
      commitFallbackOnFocus = true,
      minValue,
      onBlur,
      onFocus,
      ...rest
    },
    ref,
  ) => {
    const [inputValue, setInputValue] = useState<string>(() => formatForInput(value, precision));
    const [isFocused, setIsFocused] = useState(false);

    // Sync local string state when canonical `value` changes externally
    // (prefill, programmatic reset, parent calc updates). Preserve mid-entry
    // states that an external sync would destroy. Compare via the formatted
    // target string so that long-precision values that get truncated for
    // display don't keep retriggering the sync.
    //
    // Skip while the input is focused: the user's keystrokes are the
    // authoritative source, and re-syncing to a re-formatted parent value
    // would collapse strings like "0.000" → "0" mid-typing (LR-371).
    useEffect(() => {
      if (isFocused) return;
      if (inputValue.endsWith('.')) return;
      if (allowNegative && (inputValue === '-' || inputValue === '-0')) return;
      const targetStr = formatForInput(value, precision);
      if (inputValue === targetStr) return;
      setInputValue(targetStr);
      // Intentionally omit inputValue from deps: this effect syncs FROM
      // value TO inputValue. Adding inputValue would create a feedback loop.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, precision, allowNegative, isFocused]);

    const regex = buildRegex(precision, allowNegative);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let valStr = e.target.value;
      // Empty string is allowed past the regex (regex requires at least the
      // sign or a digit/dot); negative-only "-" is already accepted by the
      // regex when allowNegative=true.
      if (valStr !== '' && !regex.test(valStr)) return;

      if (valStr.length > 1 && valStr.startsWith('0') && valStr[1] !== '.') {
        valStr = valStr.replace(/^0+/, '');
        if (valStr === '' || valStr.startsWith('.')) valStr = '0' + valStr;
      }
      if (allowNegative && valStr.length > 2 && valStr.startsWith('-0') && valStr[2] !== '.') {
        valStr = '-' + valStr.substring(2).replace(/^0+/, '');
        if (valStr === '-' || valStr.startsWith('-.')) valStr = '-0' + valStr.substring(1);
      }

      setInputValue(valStr);

      if (valStr === '') {
        onChange(undefined);
        return;
      }
      if (isIntermediate(valStr, allowNegative)) return;
      const parsed = parseFloat(valStr);
      if (!Number.isNaN(parsed)) onChange(parsed);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      if (inputValue !== '' && isIntermediate(inputValue, allowNegative)) {
        setInputValue(formatForInput(value, precision));
      } else if (!commitFallbackOnFocus && value === undefined) {
        // Focus prefilled inputValue purely for display (no onChange). If
        // the user blurred without an actual edit, drop the local state
        // back to empty so the fallback display takes over again.
        setInputValue('');
      } else if (minValue !== undefined && value !== undefined && value < minValue) {
        // Below the minimum acceptable value (e.g. user typed "0" into a
        // rate field). Clear local state and bubble undefined so the parent
        // calc falls back to its canonical / live source — otherwise display
        // would show the sub-min value while calc silently ignored it.
        setInputValue('');
        onChange(undefined);
      }
      onBlur?.(e);
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      // When the field was showing a fallback (empty + value undefined), pull
      // that fallback into local state so the user sees the value they were
      // about to edit. With commitFallbackOnFocus=true (default) we also
      // commit it via onChange so the user can tweak the calculated value as
      // a normal numeric entry. With commitFallbackOnFocus=false the prefill
      // is purely visual; only an actual edit propagates upward, which keeps
      // parent state untouched when the user just clicked to inspect.
      if (inputValue === '' && value === undefined && fallbackValue !== undefined) {
        const prefill = formatForInput(fallbackValue, precision);
        if (prefill !== '') {
          setInputValue(prefill);
          if (commitFallbackOnFocus) {
            onChange(parseFloat(prefill));
          }
        }
      }
      e.target.select();
      onFocus?.(e);
    };

    const showFallback =
      !isFocused && inputValue === '' && value === undefined && fallbackValue !== undefined;
    const displayValue = showFallback ? formatForInput(fallbackValue, precision) : inputValue;

    return (
      <input
        {...rest}
        ref={ref}
        type="text"
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
      />
    );
  },
);

DecimalInput.displayName = 'DecimalInput';
